import {
  Button,
  RadioButton,
  Heading,
  Spinner,
  ErrorMessage,
  SuccessPopup,
} from "@/common/components";
import { useCallback, useContext, useState, useEffect } from "@/common/hooks";
import { PublicContext } from "@/routes/PublicRoute";
import {
  useGetAccessProfilesForEnrollmentQuery,
  useGetInsuranceCompaniesForEnrollmentQuery,
  useGetStatesForEnrollmentQuery,
  useLazyGetProductManufacturersQuery,
  useProcessEnrollmentMutation,
} from "@/service/APIService";
import { entityData, scrollToTop } from "@/utils/Utilities";
import AccessOptions from "@/components/Common/AccessOptions";
import SecurityInformation from "@/components/Common/SecurityInformation";
import ContactInformation from "@/components/Common/ContactInformation";

export default function RequestNewOperator() {
  const { labels, cancel, debounceState, debounceErrors, cloneDeep } =
    useContext(PublicContext);

  const defaultIdObj = {
    accessProfileId: "",
    productMfgId: "",
  };
  const [selectedEntity, setSelectedEntity] = useState("I");
  const [securityInfo, setSecurityInfo] = useState({});
  const [contactInfo, setContactInfo] = useState({});
  const [selectedIds, setSelectedIds] = useState({
    I: [cloneDeep(defaultIdObj)],
    P: [cloneDeep(defaultIdObj)],
    R: [cloneDeep(defaultIdObj)],
  });
  const [companyType, setCompanyType] = useState("");
  const [allProductManufacturers, setAllProductManufacturers] = useState([]);
  const [productManufacturers, setProductManufacturers] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const {
    data: accessProfiles,
    isFetching,
    error,
  } = useGetAccessProfilesForEnrollmentQuery();

  const {
    data: insuranceCompanies,
    isFetching: isFetching1,
    error: error1,
  } = useGetInsuranceCompaniesForEnrollmentQuery();

  const [productManufacturersAPI, { isFetching: isFetching2, error: error2 }] =
    useLazyGetProductManufacturersQuery();

  const {
    data: states,
    isFetching: isFetching3,
    error: error3,
  } = useGetStatesForEnrollmentQuery();

  const [processEnrollment, { isLoading: isFetching4, error: error4 }] =
    useProcessEnrollmentMutation();

  const doSave = () => {
    const payload = {
      representingEntity: selectedEntity,
      insuranceCompanyId: companyType,
      ...securityInfo,
      ...contactInfo,
      productMfgIds: selectedIds[selectedEntity]
        .filter((e2) => e2.accessProfileId !== "")
        .map((e) => e.productMfgId),
      accessProfileIds: selectedIds[selectedEntity]
        .filter((e) => e.accessProfileId !== "")
        .map((e2) => e2.accessProfileId),
    };
    payload.customUserId = payload.customUserId.toLowerCase();
    processEnrollment(payload)
      .unwrap()
      .then(() => {
        setShowPopup(true);
      })
      .catch(() => {
        scrollToTop();
      });
  };

  const checkDisabled = useCallback(() => {
    let disable = false;

    Object.keys(errors).forEach((e) => {
      if (errors[e]) {
        disable = true;
      }
    });

    if (disable) {
      return true;
    }

    let productMfgIds = selectedIds[selectedEntity].filter(
      (e) => e.productMfgId !== ""
    );
    let accessProfileIds = selectedIds[selectedEntity].filter(
      (e) => e.accessProfileId !== ""
    );

    if (!productMfgIds.length || !accessProfileIds.length) {
      return true;
    }

    const requiredFields = [
      "customUserId",
      "challengeQuestion1",
      "challengeQuestion1Answer",
      "challengeQuestion1ConfirmAnswer",
      "challengeQuestion2",
      "challengeQuestion2Answer",
      "challengeQuestion2ConfirmAnswer",
      "firstName",
      "lastName",
      "addressLine1",
      "city",
      "zipCode",
      "phoneNumber",
      "email",
      "confirmEmail",
    ];

    const payload = {
      ...contactInfo,
      ...securityInfo,
    };

    requiredFields.forEach((e) => {
      if (!payload[e]) {
        disable = true;
      }
    });
    return disable;
  }, [errors, selectedEntity, contactInfo, securityInfo]);

  useEffect(() => {
    if (companyType) {
      if (!allProductManufacturers.length) {
        productManufacturersAPI({}).then((res) => {
          const data = res?.data?.productSponsors;
          setAllProductManufacturers(data);
          selectedIds["P"][0].productMfgId = data[0].id;
          setSelectedIds({ ...selectedIds });
        });
      }
      productManufacturersAPI({ insuranceCompanyId: companyType }).then(
        (res) => {
          const data = res?.data?.productSponsors;
          setProductManufacturers(data);
          if (data.length) {
            selectedIds["I"][0].productMfgId = data[0].id;
            selectedIds["I"][0].accessProfileId = "";
            setSelectedIds({ ...selectedIds });
          }
        }
      );
    }
  }, [companyType]);

  const commonProps = {
    errors,
    setErrors,
    touched,
    setTouched,
    debounceState,
    debounceErrors,
    labels: labels?.requestNewOperator,
  };

  return (
    <section className="container-medium py-5 px-3">
      {(isFetching ||
        isFetching1 ||
        isFetching2 ||
        isFetching3 ||
        isFetching4) && <Spinner />}
      <ErrorMessage
        error={error || error1 || error2 || error3 || error4}
        wrapperClassName="mb-3"
      />
      <Heading
        text={labels?.requestNewOperator?.requestNewAccess}
        wrapperClassNames="text-center mb-5"
      />
      <RadioButton
        data={entityData}
        value={selectedEntity}
        selected={(e) => setSelectedEntity(e)}
        wrapperClassName="mb-4"
      />

      <section className="mb-5">
        <AccessOptions
          selectedEntity={selectedEntity}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          accessProfiles={accessProfiles}
          insuranceCompanies={insuranceCompanies}
          allProductManufacturers={allProductManufacturers}
          productManufacturers={productManufacturers}
          companyType={companyType}
          setCompanyType={setCompanyType}
          approveAccessProfile={{}}
          labels={labels?.requestNewOperator}
        />
      </section>

      <SecurityInformation
        securityInfo={securityInfo}
        setSecurityInfo={setSecurityInfo}
        {...commonProps}
      />
      <ContactInformation
        contactInfo={contactInfo}
        setContactInfo={setContactInfo}
        extensionMaxLength={5}
        states={states}
        {...commonProps}
      />

      <div className="d-flex flex-row gap-1 justify-content-end">
        <Button
          id="cancel"
          text={labels?.requestNewOperator?.cancel}
          type="secondary"
          wrapperClassName="col-md-4 col-6"
          click={cancel}
        />
        <Button
          id="sendRequest"
          text={labels?.requestNewOperator?.sendRequest}
          wrapperClassName="col-md-4 col-6"
          click={doSave}
          disabled={checkDisabled()}
        />
      </div>

      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.requestNewOperator?.goToLogin}
        click={cancel}
        body={labels?.requestNewOperator?.successMessage}
      />
    </section>
  );
}
